import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { LanguageContext } from '../../contexts/LanguageContext';
import { ThemeContext } from '../../contexts/ThemeContext';

const PlatformEngineering = () => {
  const { translations } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      mirror: false,
      easing: 'ease-out-cubic'
    });
    
    return () => {
      AOS.refresh();
    };
  }, []);
  
  return (
    <div className="py-16 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-extrabold text-gray-800 dark:text-blue-50 mb-8" data-aos="fade-up">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-teal-500">{translations['platform.title'].split(' ')[0]}</span> {translations['platform.title'].split(' ')[1]}
          </h1>
          
          <div className="bg-gradient-to-r from-indigo-600 via-blue-700 to-teal-600 text-white p-8 md:p-10 rounded-xl shadow-xl mb-12 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl" data-aos="zoom-in">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">{translations['platform.hero.title']}</h2>
            <p className="text-lg md:text-xl">
              {translations['platform.hero.description']}
            </p>
          </div>
          
          <div className="mb-12" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-6 flex items-center">
              <span className="inline-block mr-3 bg-blue-600 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              </span>
              {translations['platform.excellence.title']}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-6">
              {translations['platform.excellence.description']}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-blue-600" data-aos="fade-right" data-aos-delay="100">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-blue-600 mr-2">⟡</span> {translations['platform.infrastructure.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['platform.infrastructure.description']}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-teal-600" data-aos="fade-left" data-aos-delay="200">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-teal-600 mr-2">⟡</span> {translations['platform.developer.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['platform.developer.description']}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-indigo-600" data-aos="fade-right" data-aos-delay="300">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-yellow-600 mr-2">⟡</span> {translations['platform.standardization.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['platform.standardization.description']}
                </p>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border-l-4 border-purple-600" data-aos="fade-left" data-aos-delay="400">
                <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-3 flex items-center">
                  <span className="text-red-600 mr-2">⟡</span> {translations['platform.automation.title']}
                </h4>
                <p className="text-gray-600 dark:text-gray-300">
                  {translations['platform.automation.description']}
                </p>
              </div>
            </div>
          </div>
          
          <div className="mb-16" data-aos="fade-up">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-200 mb-6 flex items-center">
              <span className="inline-block mr-3 bg-teal-600 rounded-full p-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </span>
              {translations['platform.approach.title']}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed mb-8">
              {translations['platform.approach.description']}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="100">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-4 text-white font-bold text-lg flex items-center">
                  <span className="bg-white/20 h-8 w-8 rounded-full flex items-center justify-center mr-3 font-bold">1</span>
                  {translations['platform.approach.step1.title']}
                </div>
                <div className="p-5 flex-grow">
                  <p className="text-gray-600 dark:text-gray-300">
                    {translations['platform.approach.step1.description']}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="200">
                <div className="bg-gradient-to-br from-teal-600 to-teal-700 p-4 text-white font-bold text-lg flex items-center">
                  <span className="bg-white/20 h-8 w-8 rounded-full flex items-center justify-center mr-3 font-bold">2</span>
                  {translations['platform.approach.step2.title']}
                </div>
                <div className="p-5 flex-grow">
                  <p className="text-gray-600 dark:text-gray-300">
                    {translations['platform.approach.step2.description']}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="300">
                <div className="bg-gradient-to-br from-indigo-600 to-indigo-700 p-4 text-white font-bold text-lg flex items-center">
                  <span className="bg-white/20 h-8 w-8 rounded-full flex items-center justify-center mr-3 font-bold">3</span>
                  {translations['platform.approach.step3.title']}
                </div>
                <div className="p-5 flex-grow">
                  <p className="text-gray-600 dark:text-gray-300">
                    {translations['platform.approach.step3.description']}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="400">
                <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-4 text-white font-bold text-lg flex items-center">
                  <span className="bg-white/20 h-8 w-8 rounded-full flex items-center justify-center mr-3 font-bold">4</span>
                  {translations['platform.approach.step4.title']}
                </div>
                <div className="p-5 flex-grow">
                  <p className="text-gray-600 dark:text-gray-300">
                    {translations['platform.approach.step4.description']}
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1" data-aos="fade-up" data-aos-delay="500">
                <div className="bg-gradient-to-br from-pink-600 to-pink-700 p-4 text-white font-bold text-lg flex items-center">
                  <span className="bg-white/20 h-8 w-8 rounded-full flex items-center justify-center mr-3 font-bold">5</span>
                  {translations['platform.approach.step5.title']}
                </div>
                <div className="p-5 flex-grow">
                  <p className="text-gray-600 dark:text-gray-300">
                    {translations['platform.approach.step5.description']}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-12" data-aos="fade-up">
            <div className="inline-block relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-teal-600 rounded-lg blur opacity-60 group-hover:opacity-100 transition duration-300"></div>
              <Link 
                to="/contact-us" 
                className="relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-teal-600 text-white font-bold text-lg rounded-lg shadow-xl hover:shadow-2xl transform transition duration-300 hover:-translate-y-1"
              >
                <span className="mr-3">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </span>
                {translations['platform.cta']}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformEngineering;
